import { WORKSPACE_ADDRESS } from "./schemas/workspaces";

/**
 * Link's hosts (Impa docs/PLATFORM_CONVENTIONS.md, rules 2 and 8): `ROOT_DOMAIN` is where people sign
 * in and choose a workspace, and each workspace is `<slug>.<ROOT_DOMAIN>`, its console at `/admin` and
 * its API beside it, split by path. Development is the same one level down, on `link.localhost:5173`.
 *
 * The host names the workspace a request is about and never admits anybody to it: that is the
 * session's, which must hold it, and Impa's live decision (src/domains/workspaces/access.ts). The Worker and
 * the page read hosts by these same rules: the page from its build's VITE_ROOT_DOMAIN, held to the
 * Worker's ROOT_DOMAIN by src/worker/production-config.test.ts.
 */

/** The workspace a host is, by its slug: one label under ROOT_DOMAIN. Null on the central host and any other. */
export function workspaceSlugOf(rootDomain: string, url: URL): string | null {
  const root = rootDomain.toLowerCase();
  const host = url.host.toLowerCase();
  if (!host.endsWith(`.${root}`)) return null;
  const slug = host.slice(0, -(root.length + 1));
  return WORKSPACE_ADDRESS.test(slug) ? slug : null;
}

/** Whether a request is to the central host, where sign-in and the list of workspaces are. */
export function isCentralHost(rootDomain: string, url: URL): boolean {
  return url.host.toLowerCase() === rootDomain.toLowerCase();
}

/**
 * The domain the session cookie is set on, so the central host and every workspace's host share it:
 * ROOT_DOMAIN without its port, Sustain's rule (its src/lib/session.ts, cookieDomainFor). A cookie's
 * domain carries no port. A request to a host outside ROOT_DOMAIN (a preview address), or a
 * ROOT_DOMAIN of one label, gets a host-only cookie instead: undefined.
 */
export function cookieDomainOf(rootDomain: string, url: URL): string | undefined {
  const root = rootDomain.toLowerCase().replace(/:\d+$/, "");
  if (!root.includes(".")) return undefined;
  const host = url.hostname.toLowerCase();
  return host === root || host.endsWith(`.${root}`) ? root : undefined;
}

/** A workspace's console, on its own host, on the scheme the central host uses (APP_URL). */
export function workspaceUrl(appUrl: string, rootDomain: string, slug: string, path = "/admin"): string {
  const central = new URL(appUrl);
  return new URL(path, `${central.protocol}//${slug}.${rootDomain}`).href;
}
